import { cloneElement, useId, type ReactElement, type ReactNode } from 'react';

/**
 * Campo de formulario con la etiqueta enlazada al control (`htmlFor`) y la
 * ayuda o el error anunciados por el lector de pantalla. El `Field` común no
 * enlaza la etiqueta; aquí sí, porque el panel es casi todo formularios.
 */
export function FormField({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactElement<Record<string, unknown>>;
}): JSX.Element {
  const auto = useId();
  const id = (children.props.id as string | undefined) ?? auto;
  const describedBy = error || hint ? `${id}-desc` : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {error ? (
        <div className="error" id={describedBy}>
          {error}
        </div>
      ) : hint ? (
        <div className="hint" id={describedBy}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** Casilla con su texto y, debajo, una explicación corta. */
export function CheckField({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="field">
      <label className="check">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {hint ? <div className="hint check-hint">{hint}</div> : null}
    </div>
  );
}
